import { useEffect, useState } from 'react';

type UseLocalStorageStateOptions<T> = {
  validate?: (value: T) => boolean;
  serialize?: (value: T) => string;
  deserialize?: (value: string) => T;
};

/**
 * Synchronizes React state with localStorage while supporting validation.
 * Avoids redundant writes by only updating storage when the state changes.
 */
export function useLocalStorageState<T>(
  key: string,
  defaultValue: T | null,
  options?: UseLocalStorageStateOptions<T>
) {
  const { validate, serialize, deserialize } = options || {};

  const [state, setState] = useState<T | null>(() => {
    const stored = localStorage.getItem(key);
    if (stored === null) return defaultValue;

    try {
      const value = deserialize ? deserialize(stored) : (stored as unknown as T);
      if (validate && !validate(value)) {
        return defaultValue;
      }
      return value;
    } catch (error) {
      console.warn(`Failed to parse localStorage key "${key}"`, error);
      return defaultValue;
    }
  });

  useEffect(() => {
    if (state === null || state === undefined) {
      localStorage.removeItem(key);
      return;
    }

    try {
      const serialized = serialize ? serialize(state) : String(state);
      localStorage.setItem(key, serialized);
    } catch (error) {
      console.warn(`Failed to persist localStorage key "${key}"`, error);
    }
  }, [key, serialize, state]);

  return [state, setState] as const;
}
